import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Card,
  CardContent,
  Divider,
  Snackbar,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { backendApiService } from '../../services/backendApiService';
import { urlService } from '../../services/urlService';
import { keywordService } from '../../services/keywordService';
import { fetchWebsites, setSelectedWebsite } from '../../store/slices/websiteSlice';
import { getCountryFlag, cleanUrl } from '../../utils/countryFlags';

interface MetaDescription {
  text: string;
  character_count: number;
}

interface KeywordSuggestion {
  keyword: string;
  avgMonthlySearches?: number;
}

const MetaDescriptionGenerator: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const websites = useSelector((state: RootState) => state.website.websites);
  
  // Form state
  const [content, setContent] = useState('');
  const [primaryKeyword, setPrimaryKeyword] = useState('');
  const [secondaryKeywords, setSecondaryKeywords] = useState('');
  const [language, setLanguage] = useState('English');
  const [maxLength, setMaxLength] = useState(155);
  const [selectedUrl, setSelectedUrl] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<number | null>(null);
  
  // Data state
  const [descriptions, setDescriptions] = useState<MetaDescription[]>([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState<KeywordSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);
  const [useGoogleAds, setUseGoogleAds] = useState(true);
  
  // Language options
  const languages = [
    'English', 'Spanish', 'French', 'German', 'Italian', 
    'Dutch', 'Portuguese', 'Japanese', 'Chinese', 'Korean'
  ];

  // Load websites on component mount
  useEffect(() => {
    if (websites.length === 0) {
      dispatch(fetchWebsites());
    }
  }, [dispatch, websites.length]);

  // Load URLs when website changes
  useEffect(() => {
    const loadUrls = async () => {
      if (!selectedWebsite?.website_id) return;
      
      try {
        const { urls: urlData, error } = await urlService.getUrlsByWebsite(selectedWebsite.website_id);
        
        if (error) {
          console.error('Error loading URLs:', error);
          setError('Failed to load URLs');
        } else {
          // Extract the full URLs from the URL objects and remove duplicates
          const urlList = urlData.map(url => url.full_url || url.url_id);
          const uniqueUrls = Array.from(new Set(urlList));
          setUrls(uniqueUrls);
        }
      } catch (err) {
        console.error('Error loading URLs:', err);
        setError('Failed to load URLs');
      }
    };
    
    loadUrls();
  }, [selectedWebsite]);

  // Fetch content when URL is selected
  useEffect(() => {
    console.log('[MetaDescription] useEffect triggered - selectedUrl:', selectedUrl, 'selectedWebsite:', selectedWebsite?.website_id);
    
    const fetchUrlContent = async () => {
      if (!selectedUrl || !selectedWebsite) {
        console.log('[MetaDescription] Skipping fetch - missing URL or website');
        return;
      }
      
      setLoading(true);
      try {
        // Call the backend to fetch content for this URL
        console.log('[MetaDescription] Fetching content for URL:', selectedUrl);
        const response = await backendApiService.post('/api/meta-description/content', {
          url: selectedUrl,
          websiteId: selectedWebsite.website_id
        });
        
        console.log('Content fetch response:', response);
        
        // The response is already the data object from axios (backendApiService returns response.data)
        if (response && response.success && response.data && response.data.content) {
          setContent(response.data.content);
          setSuccess('Content loaded successfully!');
          setError(null);
          
          // Show existing meta description if available
          if (response.data.existingMetaDescription) {
            console.log('Found existing meta description:', response.data.existingMetaDescription);
          }
        } else if (response && response.error) {
          // Show the detailed message from backend
          setError(response.message || response.error);
          setContent(''); // Clear any existing content
          // Don't show success message if content wasn't found
          setSuccess(null);
        } else {
          setError('No content found for this URL');
          setContent(''); // Clear any existing content
          setSuccess(null);
        }
      } catch (err) {
        console.error('Error fetching content:', err);
        setError('Failed to fetch content for this URL');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUrlContent();
  }, [selectedUrl, selectedWebsite]);

  // Fetch keyword suggestions when primary keyword changes
  useEffect(() => {
    const fetchKeywordSuggestions = async () => {
      if (!primaryKeyword || !useGoogleAds || primaryKeyword.length < 3) {
        setKeywordSuggestions([]);
        return;
      }
      
      setLoadingKeywords(true);
      try {
        const response = await backendApiService.post('/api/meta-description/keyword-suggestions', {
          keyword: primaryKeyword,
          country: 'US' // TODO: Make this configurable
        });
        
        // backendApiService already returns response.data
        if (response && response.success) {
          console.log('Keyword suggestions response:', response);
          const suggestions = response.data.suggestions || [];
          console.log('Setting keyword suggestions:', suggestions);
          setKeywordSuggestions(suggestions);
          setShowSuggestions(suggestions.length > 0);
        }
      } catch (err) {
        console.error('Error fetching keyword suggestions:', err);
      } finally {
        setLoadingKeywords(false);
      }
    };
    
    // Debounce the API call
    const timeoutId = setTimeout(fetchKeywordSuggestions, 500);
    return () => clearTimeout(timeoutId);
  }, [primaryKeyword, useGoogleAds]);

  const generateDescriptions = async () => {
    if (!content || !primaryKeyword) {
      setError('Content and primary keyword are required');
      return;
    }
    
    if (content.length < 50) {
      setError('Content must be at least 50 characters long');
      return;
    }
    
    setGenerating(true);
    setError(null);
    
    try {
      const secondaryKeywordsArray = secondaryKeywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);
      
      const response = await backendApiService.post('/api/meta-description/generate', {
        content,
        primaryKeyword,
        secondaryKeywords: secondaryKeywordsArray,
        language,
        maxLength
      });
      
      // backendApiService.post already returns response.data
      if (response && response.success) {
        setDescriptions(response.data.descriptions);
        setSuccess('Meta descriptions generated successfully!');
      } else {
        setError(response?.error || 'Failed to generate descriptions');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate meta descriptions');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(index);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const addKeywordSuggestion = (keyword: string) => {
    const currentKeywords = secondaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    if (!currentKeywords.includes(keyword)) {
      currentKeywords.push(keyword);
      setSecondaryKeywords(currentKeywords.join(', '));
    }
  };

  const highlightKeywords = (text: string) => {
    let highlightedText = text;
    
    // Highlight primary keyword
    if (primaryKeyword) {
      const primaryRegex = new RegExp(`(${primaryKeyword})`, 'gi');
      highlightedText = highlightedText.replace(
        primaryRegex, 
        '<span style="background-color: #FFEB3B; padding: 0 2px; border-radius: 3px;">$1</span>'
      );
    }
    
    // Highlight secondary keywords
    const secondaryKeywordsArray = secondaryKeywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    secondaryKeywordsArray.forEach(keyword => {
      const regex = new RegExp(`(${keyword})`, 'gi');
      highlightedText = highlightedText.replace(
        regex,
        '<span style="background-color: #E1F5FE; padding: 0 2px; border-radius: 3px;">$1</span>'
      );
    });
    
    return highlightedText;
  };

  const getCharacterCountColor = (count: number) => {
    if (count > maxLength) return '#ff4b4b';
    if (count > maxLength - 10) return '#ffa64b';
    return '#4bff7f';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Meta Description Generator
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Generate SEO-optimized meta descriptions using the META framework with AI assistance.
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Website
        </Typography>
        <Autocomplete
          value={selectedWebsite}
          onChange={(event, newValue) => {
            if (newValue) {
              dispatch(setSelectedWebsite(newValue));
            }
          }}
          options={websites}
          getOptionLabel={(option) => {
            const flag = getCountryFlag(option.country);
            const domain = cleanUrl(option.base_url);
            return `${flag} ${domain}`;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Search for a website..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Typography>
                {getCountryFlag(option.country)} {cleanUrl(option.base_url)}
              </Typography>
            </Box>
          )}
          sx={{ width: '100%' }}
          isOptionEqualToValue={(option, value) => option.website_id === value.website_id}
        />
      </Paper>
      
      <Grid container spacing={3}>
        {/* Left Column - Input */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 1: Enter Content
            </Typography>
            
            <Autocomplete
              fullWidth
              sx={{ mb: 2 }}
              value={selectedUrl || null}
              onChange={(event, newValue) => {
                setSelectedUrl(newValue || '');
              }}
              options={urls}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select URL (optional)"
                  variant="outlined"
                  placeholder={!selectedWebsite ? 'Select a website first' : 'Type to search URLs...'}
                  disabled={!selectedWebsite || urls.length === 0}
                />
              )}
              noOptionsText="No URLs found"
              isOptionEqualToValue={(option, value) => option === value}
            />
            
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter your content here or select a URL above to load content..."
                variant="outlined"
                sx={{ mb: 2 }}
                helperText={`${content.length} characters`}
                disabled={loading}
              />
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <CircularProgress size={30} />
                  <Typography variant="body2">Loading content...</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Right Column - Keywords */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 2: Enter Keywords
            </Typography>
            
            <TextField
              fullWidth
              value={primaryKeyword}
              onChange={(e) => setPrimaryKeyword(e.target.value)}
              label="Primary Keyword (required)"
              variant="outlined"
              sx={{ mb: 2 }}
              helperText="The main keyword that must appear in the meta description"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={useGoogleAds}
                  onChange={(e) => setUseGoogleAds(e.target.checked)}
                />
              }
              label="Use Google Ads API for keyword suggestions"
              sx={{ mb: 2 }}
            />
            
            {/* Keyword Suggestions */}
            {loadingKeywords && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {showSuggestions && keywordSuggestions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    Keyword Suggestions
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setShowSuggestions(!showSuggestions)}
                  >
                    <ExpandLessIcon />
                  </IconButton>
                </Box>
                
                <List 
                  dense 
                  sx={{ 
                    maxHeight: '300px', 
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    }
                  }}
                >
                  {keywordSuggestions.map((suggestion, index) => (
                    <ListItem key={`suggestion-${index}-${suggestion.keyword}`}>
                      <ListItemText
                        primary={suggestion.keyword}
                        secondary={
                          suggestion.avgMonthlySearches
                            ? `${suggestion.avgMonthlySearches.toLocaleString()} searches/mo`
                            : 'Search volume not available'
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => addKeywordSuggestion(suggestion.keyword)}
                        >
                          Add
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
            
            <TextField
              fullWidth
              value={secondaryKeywords}
              onChange={(e) => setSecondaryKeywords(e.target.value)}
              label="Secondary Keywords (optional)"
              variant="outlined"
              sx={{ mb: 2 }}
              helperText="Keywords will be naturally integrated into descriptions. If they don't fit naturally, related terms may be used instead."
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Language</InputLabel>
              <Select
                value={language}
                onChange={(e: SelectChangeEvent) => setLanguage(e.target.value)}
                label="Language"
              >
                {languages.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Character Limit: {maxLength}
              </Typography>
              <Slider
                value={maxLength}
                onChange={(_, value) => setMaxLength(value as number)}
                min={120}
                max={170}
                step={5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              onClick={generateDescriptions}
              disabled={loading || generating || !content || !primaryKeyword}
              startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
            >
              {generating ? 'Generating...' : 'Generate Meta Descriptions'}
            </Button>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Generated Descriptions */}
      {descriptions.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Generated Meta Descriptions
          </Typography>
          
          <Grid container spacing={2}>
            {descriptions.map((desc, index) => (
              <Grid item xs={12} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        Option {index + 1}
                      </Typography>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: getCharacterCountColor(desc.character_count) }}
                        >
                          {desc.character_count}/{maxLength} characters
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        backgroundColor: '#f5f5f5',
                        mb: 2
                      }}
                      dangerouslySetInnerHTML={{ __html: highlightKeywords(desc.text) }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={copySuccess === index ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton
                          onClick={() => copyToClipboard(desc.text, index)}
                          color={copySuccess === index ? 'success' : 'default'}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      {/* Error/Success Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={10000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ 
            whiteSpace: 'pre-line',
            maxWidth: '600px'
          }}
        >
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MetaDescriptionGenerator;